import NoteHtml, { hasNoteContent } from '../../../../../components/common/NoteHtml';
import { resolveInterestNote } from './interestNote';
import { printConfigFor } from './invoiceSettingDefaults';

const InvoicePrintPage = ({ invoice, pageIdx, totalPages, className = '' }) => {
    const config = printConfigFor(invoice);
    const society = invoice.society || {};
    const flat = invoice.flat || {};
    const member = invoice.member_details || {};
    const invoiceType = invoice.invoiceType || {};
    const series = invoice.series || {};
    const items = invoice.items || [];

    // Society address
    const primaryAddress = society.addresses?.find(a => a.is_primary === 1) || society.addresses?.[0] || {};
    const societyAddress = [
        primaryAddress.address_line1,
        primaryAddress.address_line2,
        [primaryAddress.city?.name, primaryAddress.state?.name].filter(Boolean).join(', '),
        primaryAddress.pincode ? `- ${primaryAddress.pincode}` : ''
    ].filter(Boolean).join(', ');

    const primaryContact = society.contacts?.find(c => c.is_primary === 1) || society.contacts?.[0] || {};

    // Member entity details from ownership
    const ownership = flat.ownerships?.[0] || {};
    const entity = ownership.entity || {};
    const memberPhone = entity.phone || member.phone || '';
    const memberEmail = entity.email || member.email || '';
    const memberPan = entity.pan_no || '';
    const memberGst = entity.gstin_no || member.gst_no || '';
    const memberAddress = entity.address || '';

    // Separate charge items from system/tax items
    const SYSTEM_HEAD_CODES = ['CGST', 'SGST', 'IGST', 'PAR', 'DNR', 'INT', 'RND', 'ADJ'];
    const chargeItems = items.filter(item => !SYSTEM_HEAD_CODES.includes(item.tariffHead?.head_code));
    const systemItems = items.filter(item => SYSTEM_HEAD_CODES.includes(item.tariffHead?.head_code));

    const arrearItems = systemItems.filter(item => ['PAR', 'DNR', 'INT'].includes(item.tariffHead?.head_code));
    const taxItems = systemItems.filter(item => ['CGST', 'SGST', 'IGST'].includes(item.tariffHead?.head_code));
    const otherItems = systemItems.filter(item => ['RND', 'ADJ'].includes(item.tariffHead?.head_code));

    const formatCurrency = (amount) => {
        const val = parseFloat(amount || 0);
        return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatPeriod = (start, end) => {
        if (!start) return '-';
        const s = new Date(start);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const startStr = `${months[s.getMonth()]} ${s.getFullYear()}`;
        if (!end) return startStr;
        const e = new Date(end);
        const endStr = `${months[e.getMonth()]} ${e.getFullYear()}`;
        return startStr === endStr ? startStr : `${startStr} - ${endStr}`;
    };

    const documentNumber = invoice.document_number || `${series.prefix || ''}${invoice.invoice_id}`;
    const netPayable = parseFloat(invoice.net_payable || 0);
    const amountPaid = parseFloat(invoice.amount_paid || 0);
    const balanceDue = parseFloat(invoice.balance_due || 0);
    const taxAmount = parseFloat(invoice.tax_amount || 0);
    const arrearsAmount = parseFloat(invoice.arrears_amount || 0);
    const interestAmount = parseFloat(invoice.interest_amount || 0);
    // Interest charged on this bill: the header figure plus any INT line item
    // that is not already counted there.
    const interestLineTotal = (invoice.items || [])
        .filter((i) => i.tariffHead?.head_code === 'INT')
        .reduce((t, i) => t + parseFloat(i.base_amount || i.system_amount || 0), 0);
    const totalInterest = interestAmount || interestLineTotal;
    // Everything else on the bill - charges, arrears, tax, round off.
    const totalPrincipal = parseFloat(invoice.net_payable || 0) - totalInterest;
    const roundOffAmount = parseFloat(invoice.round_off_amount || 0);
    const adjustmentAmount = parseFloat(invoice.adjustment_amount || 0);
    const discountAmount = parseFloat(invoice.discount_amount || 0);
    const chargeSubtotal = chargeItems.reduce((sum, item) => sum + parseFloat(item.base_amount || item.system_amount || 0), 0);

    const numberToWords = (num) => {
        if (num === 0) return 'Zero';
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
            'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const convertLessThanThousand = (n) => {
            if (n === 0) return '';
            if (n < 20) return ones[n];
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
            return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convertLessThanThousand(n % 100) : '');
        };
        const absNum = Math.abs(Math.round(num));
        if (absNum === 0) return 'Zero';
        let result = '';
        const crore = Math.floor(absNum / 10000000);
        const lakh = Math.floor((absNum % 10000000) / 100000);
        const thousand = Math.floor((absNum % 100000) / 1000);
        const remainder = absNum % 1000;
        if (crore) result += convertLessThanThousand(crore) + ' Crore ';
        if (lakh) result += convertLessThanThousand(lakh) + ' Lakh ';
        if (thousand) result += convertLessThanThousand(thousand) + ' Thousand ';
        if (remainder) result += convertLessThanThousand(remainder);
        return (num < 0 ? 'Minus ' : '') + result.trim() + ' Rupees Only';
    };

    const getStatusStyles = (status) => {
        switch (status) {
            case 'PAID': return { background: '#d1fae5', color: '#065f46' };
            case 'POSTED': return { background: '#dbeafe', color: '#1e40af' };
            case 'VOID': return { background: '#fee2e2', color: '#991b1b' };
            case 'CANCELLED': return { background: '#f3f4f6', color: '#374151' };
            case 'PARTIAL': return { background: '#fef3c7', color: '#92400e' };
            default: return { background: '#f3f4f6', color: '#374151' };
        }
    };

    const statusStyles = getStatusStyles(invoice.status);
    const printFontSize = config.print_font_size ? `${config.print_font_size}px` : '12px';

    return (
        <div className={className} style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", fontSize: printFontSize }}>

            {/* Header - Society Info + Invoice Number */}
            <div style={{ borderBottom: '3px solid #4338ca', padding: '20px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ maxWidth: '60%' }}>
                    <h1 style={{ fontSize: config.header_font_size ? `${config.header_font_size}px` : '22px', fontWeight: 700, color: '#1e1b4b', margin: 0, letterSpacing: '-0.3px' }}>
                        {config.invoice_header || invoiceType.invoice_title || 'Invoice'}
                    </h1>
                    <p style={{ fontSize: '14px', color: '#4338ca', fontWeight: 700, margin: '4px 0 0 0' }}>
                        {society.name || ''}
                    </p>
                    {societyAddress && (
                        <p style={{ fontSize: '10px', color: '#6b7280', margin: '4px 0 0 0', lineHeight: '1.4' }}>
                            {societyAddress}
                        </p>
                    )}
                    {society.registration_number && (
                        <p style={{ fontSize: '10px', color: '#6b7280', margin: '2px 0 0 0' }}>
                            Reg. No: {society.registration_number}
                        </p>
                    )}
                    {(primaryContact.phone || primaryContact.mobile) && (
                        <p style={{ fontSize: '10px', color: '#6b7280', margin: '2px 0 0 0' }}>
                            Tel: {primaryContact.phone || ''}{primaryContact.phone && primaryContact.mobile ? ' | ' : ''}{primaryContact.mobile || ''}
                            {primaryContact.email ? ` | ${primaryContact.email}` : ''}
                        </p>
                    )}
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Invoice No.</div>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e1b4b', margin: '2px 0' }}>{documentNumber}</div>
                    <div style={{
                        display: 'inline-block',
                        padding: '3px 12px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        ...statusStyles
                    }}>
                        {invoice.status}
                    </div>
                    {invoice.is_carried_forward === 1 && (
                        <div style={{ marginTop: '4px' }}>
                            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '8px', fontSize: '9px', fontWeight: 600, background: '#fff7ed', color: '#c2410c' }}>
                                Carried Forward
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Date & Period Row */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>
                <div style={{ flex: 1, padding: '10px 28px', borderRight: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Bill Date</span>
                    <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{formatDate(invoice.bill_date)}</div>
                </div>
                <div style={{ flex: 1, padding: '10px 28px', borderRight: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Due Date</span>
                    <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{formatDate(invoice.due_date)}</div>
                </div>
                <div style={{ flex: 1, padding: '10px 28px' }}>
                    <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Billing Period</span>
                    <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{formatPeriod(invoice.period_start, invoice.period_end)}</div>
                </div>
            </div>

            {/* Bill To / Unit Info */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ flex: 1, padding: '14px 28px', borderRight: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Bill To</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{member.name || '-'}</div>
                    {config.show_phone === 1 && memberPhone && (
                        <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '3px' }}>Phone: {memberPhone}</div>
                    )}
                    {config.show_email === 1 && memberEmail && (
                        <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>Email: {memberEmail}</div>
                    )}
                    {config.show_pan_card_no === 1 && memberPan && (
                        <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>PAN: {memberPan}</div>
                    )}
                    {config.show_gst_no === 1 && memberGst && (
                        <div style={{ fontSize: '10px', color: '#4338ca', marginTop: '2px', fontWeight: 600 }}>GSTIN: {memberGst}</div>
                    )}
                    {config.show_flat_address === 1 && memberAddress && (
                        <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '3px', lineHeight: '1.3' }}>{memberAddress}</div>
                    )}
                </div>
                <div style={{ flex: 1, padding: '14px 28px' }}>
                    <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Unit Details</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div>
                            <span style={{ fontSize: '10px', color: '#9ca3af' }}>Unit: </span>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>
                                {flat.wing?.title ? `${flat.wing.title}-` : ''}{flat.flat_number || member.flat_no || '-'}
                            </span>
                        </div>
                        {flat.floor_number !== undefined && flat.floor_number !== null && (
                            <div>
                                <span style={{ fontSize: '10px', color: '#9ca3af' }}>Floor: </span>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>{flat.floor_number}</span>
                            </div>
                        )}
                        {flat.flat_type && (
                            <div>
                                <span style={{ fontSize: '10px', color: '#9ca3af' }}>Type: </span>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>{flat.flat_type}</span>
                            </div>
                        )}
                        {config.show_flat_area === 1 && (flat.carpet_area || member.flat_area) && (
                            <div>
                                <span style={{ fontSize: '10px', color: '#9ca3af' }}>{config.area_label || 'Area'}: </span>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>{flat.carpet_area || member.flat_area} sq.ft</span>
                            </div>
                        )}
                        {config.show_parking_no === 1 && flat.is_parking_allotted === 1 && (
                            <div>
                                <span style={{ fontSize: '10px', color: '#9ca3af' }}>Parking: </span>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>{flat.parking_slot_count || 0} slot(s)</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Invoice Items Table */}
            <div style={{ padding: '0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                        <tr style={{ background: '#f8fafc' }}>
                            <th style={{ textAlign: 'left', padding: '8px 28px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '30px' }}>#</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>Description</th>
                            {config.hide_tariff_head !== 1 && (
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '80px' }}>Head</th>
                            )}
                            <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '50px' }}>Qty</th>
                            <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '90px' }}>Rate</th>
                            {config.show_detailed_gst === 1 && (
                                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '70px' }}>Tax</th>
                            )}
                            <th style={{ textAlign: 'right', padding: '8px 28px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '100px' }}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {chargeItems.map((item, idx) => {
                            const rate = parseFloat(item.rate) || 0;
                            const baseAmount = parseFloat(item.base_amount || item.system_amount || 0);
                            return (
                                <tr key={item.item_id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '8px 28px', color: '#9ca3af' }}>{idx + 1}</td>
                                    <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1f2937' }}>
                                        {item.tariffHead?.head_name || item.description || '-'}
                                        {item.description && item.tariffHead?.head_name && item.description !== item.tariffHead.head_name && (
                                            <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 400, marginTop: '1px' }}>{item.description}</div>
                                        )}
                                        {config.show_detailed_gst === 1 && item.taxRule && (
                                            <div style={{ fontSize: '9px', color: '#6366f1', fontWeight: 500, marginTop: '1px' }}>
                                                {item.taxRule.rule_name}
                                            </div>
                                        )}
                                    </td>
                                    {config.hide_tariff_head !== 1 && (
                                        <td style={{ padding: '8px 12px', color: '#9ca3af', fontSize: '10px' }}>{item.tariffHead?.head_code || '-'}</td>
                                    )}
                                    <td style={{ padding: '8px 12px', textAlign: 'center', color: '#374151' }}>{item.quantity}</td>
                                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#374151' }}>{formatCurrency(rate > 0 ? rate : baseAmount)}</td>
                                    {config.show_detailed_gst === 1 && (
                                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#6b7280' }}>{formatCurrency(item.tax_amount)}</td>
                                    )}
                                    <td style={{ padding: '8px 28px', textAlign: 'right', fontWeight: 700, color: '#111827' }}>{formatCurrency(baseAmount)}</td>
                                </tr>
                            );
                        })}
                        {chargeItems.length === 0 && (
                            <tr>
                                <td colSpan={config.show_detailed_gst === 1 ? (config.hide_tariff_head !== 1 ? 7 : 6) : (config.hide_tariff_head !== 1 ? 6 : 5)} style={{ padding: '16px 28px', textAlign: 'center', color: '#9ca3af' }}>
                                    No charge items
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Arrears & System Charges Section */}
            {config.arrear_display === 1 && arrearItems.length > 0 && arrearItems.some(item => parseFloat(item.base_amount || item.system_amount || 0) !== 0) && (
                <div style={{ borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ background: '#fffbeb', padding: '8px 28px', borderBottom: '1px solid #fde68a' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Arrears & Interest</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <tbody>
                            {arrearItems.map((item, idx) => {
                                const amount = parseFloat(item.base_amount || item.system_amount || 0);
                                if (amount === 0) return null;
                                const code = item.tariffHead?.head_code;
                                if (code === 'PAR' && config.hide_principal_arrear === 1) return null;
                                if (code === 'INT' && config.hide_interest_arrear === 1) return null;
                                return (
                                    <tr key={item.item_id || idx} style={{ borderBottom: '1px solid #fef3c7' }}>
                                        <td style={{ padding: '6px 28px', fontWeight: 600, color: '#92400e', width: '50%' }}>
                                            {item.tariffHead?.head_name || item.description}
                                        </td>
                                        <td style={{ padding: '6px 28px', textAlign: 'right', fontWeight: 700, color: amount < 0 ? '#dc2626' : '#92400e' }}>
                                            {amount < 0 ? '- ' : ''}{formatCurrency(Math.abs(amount))}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Summary Section */}
            <div style={{ borderTop: '2px solid #e2e8f0', display: 'flex' }}>
                {/* Left: Amount in words + Notes */}
                <div style={{ flex: 1, padding: '14px 28px', borderRight: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Amount in Words</div>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', fontStyle: 'italic' }}>
                        {numberToWords(netPayable)}
                    </div>

                    {config.show_invoice_note === 1 && hasNoteContent(config.invoice_notes) && (
                        <div style={{ marginTop: '10px' }}>
                            <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Note</div>
                            <NoteHtml html={config.invoice_notes} style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.4' }} />
                        </div>
                    )}

                    {resolveInterestNote(config, interestAmount) && (
                        <div style={{ marginTop: '8px' }}>
                            <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Interest Note</div>
                            <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.4' }}>{resolveInterestNote(config, interestAmount)}</div>
                        </div>
                    )}

                    {invoice.remarks && (
                        <div style={{ marginTop: '8px' }}>
                            <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Remarks</div>
                            <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.4' }}>{invoice.remarks}</div>
                        </div>
                    )}

                    {invoice.notes && (
                        <div style={{ marginTop: '8px' }}>
                            <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Notes</div>
                            <NoteHtml html={invoice.notes} style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.4' }} />
                        </div>
                    )}
                </div>

                {/* Right: Summary breakdown */}
                <div style={{ width: '260px', padding: '14px 28px' }}>
                    {/* Subtotal */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                        <span style={{ color: '#6b7280' }}>Subtotal</span>
                        <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(chargeSubtotal)}</span>
                    </div>

                    {/* Discount */}
                    {discountAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                            <span style={{ color: '#059669' }}>Discount</span>
                            <span style={{ fontWeight: 600, color: '#059669' }}>-{formatCurrency(discountAmount)}</span>
                        </div>
                    )}

                    {/* Tax items (CGST, SGST, IGST) */}
                    {taxItems.map((item, idx) => {
                        const amount = parseFloat(item.base_amount || item.system_amount || 0);
                        if (amount === 0) return null;
                        return (
                            <div key={item.item_id || idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                <span style={{ color: '#6b7280' }}>{item.tariffHead?.head_name || item.description}</span>
                                <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(amount)}</span>
                            </div>
                        );
                    })}

                    {/* Show total tax if not detailed */}
                    {config.show_detailed_gst !== 1 && taxAmount > 0 && taxItems.length === 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                            <span style={{ color: '#6b7280' }}>Tax</span>
                            <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(taxAmount)}</span>
                        </div>
                    )}

                    {/* Arrears summary in sidebar */}
                    {config.arrear_display === 1 && arrearsAmount > 0
                        && !arrearItems.some(i => ['PAR', 'DNR'].includes(i.tariffHead?.head_code)
                            && parseFloat(i.base_amount || i.system_amount || 0) !== 0) && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                            <span style={{ color: '#92400e' }}>Arrears</span>
                            <span style={{ fontWeight: 600, color: '#92400e' }}>{formatCurrency(arrearsAmount)}</span>
                        </div>
                    )}

                    {config.arrear_display === 1 && interestAmount > 0
                        && !arrearItems.some(i => i.tariffHead?.head_code === 'INT'
                            && parseFloat(i.base_amount || i.system_amount || 0) !== 0) && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                            <span style={{ color: '#92400e' }}>Interest</span>
                            <span style={{ fontWeight: 600, color: '#92400e' }}>{formatCurrency(interestAmount)}</span>
                        </div>
                    )}

                    {/* Show Total Principal / Show Total Interest (Functional Settings
                        > General). These only suppressed a zero arrears row before,
                        so toggling them changed nothing visible. Each now prints its
                        own line: principal is everything billed except interest. */}
                    {config.show_total_principal === 1 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px', borderTop: '1px dashed #e5e7eb' }}>
                            <span style={{ color: '#6b7280' }}>Total Principal</span>
                            <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(totalPrincipal)}</span>
                        </div>
                    )}

                    {config.show_total_interest === 1 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                            <span style={{ color: '#92400e' }}>Total Interest</span>
                            <span style={{ fontWeight: 600, color: '#92400e' }}>{formatCurrency(totalInterest)}</span>
                        </div>
                    )}

                    {/* Other system items (Round Off, Adjustment) */}
                    {otherItems.map((item, idx) => {
                        const amount = parseFloat(item.base_amount || item.system_amount || 0);
                        if (amount === 0) return null;
                        return (
                            <div key={item.item_id || idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                <span style={{ color: '#6b7280' }}>{item.tariffHead?.head_name || item.description}</span>
                                <span style={{ fontWeight: 600, color: amount < 0 ? '#dc2626' : '#374151' }}>
                                    {amount < 0 ? '-' : ''}{formatCurrency(Math.abs(amount))}
                                </span>
                            </div>
                        );
                    })}

                    {/* Invoice-level round off if not in items */}
                    {roundOffAmount !== 0 && !otherItems.some(i => i.tariffHead?.head_code === 'RND') && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                            <span style={{ color: '#6b7280' }}>Round Off</span>
                            <span style={{ fontWeight: 600, color: roundOffAmount < 0 ? '#dc2626' : '#374151' }}>
                                {roundOffAmount < 0 ? '-' : ''}{formatCurrency(Math.abs(roundOffAmount))}
                            </span>
                        </div>
                    )}

                    {/* Invoice-level adjustment if not in items */}
                    {adjustmentAmount !== 0 && !otherItems.some(i => i.tariffHead?.head_code === 'ADJ') && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                            <span style={{ color: '#6b7280' }}>Adjustment</span>
                            <span style={{ fontWeight: 600, color: adjustmentAmount < 0 ? '#dc2626' : '#374151' }}>
                                {adjustmentAmount < 0 ? '-' : ''}{formatCurrency(Math.abs(adjustmentAmount))}
                            </span>
                        </div>
                    )}

                    {/* Net Payable Total */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 4px 0', fontSize: '13px', borderTop: '2px solid #1e1b4b', marginTop: '6px' }}>
                        <span style={{ fontWeight: 700, color: '#1e1b4b' }}>Net Payable</span>
                        <span style={{ fontWeight: 800, color: '#1e1b4b' }}>Rs {formatCurrency(netPayable)}</span>
                    </div>

                    {/* Paid & Balance */}
                    {amountPaid > 0 && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                <span style={{ color: '#059669' }}>Amount Paid</span>
                                <span style={{ fontWeight: 600, color: '#059669' }}>- {formatCurrency(amountPaid)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '12px', borderTop: '1px solid #e5e7eb', marginTop: '4px' }}>
                                <span style={{ fontWeight: 700, color: balanceDue > 0 ? '#dc2626' : '#059669' }}>Balance Due</span>
                                <span style={{ fontWeight: 800, color: balanceDue > 0 ? '#dc2626' : '#059669' }}>Rs {formatCurrency(balanceDue)}</span>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div style={{ borderTop: '1px solid #e5e7eb', padding: '10px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                <div style={{ fontSize: '9px', color: '#9ca3af' }}>
                    Generated on {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
                <div style={{ fontSize: '9px', color: '#9ca3af' }}>
                    {society.name}{invoiceType.invoice_title ? ` | ${invoiceType.invoice_title}` : ''}
                    {totalPages > 1 ? ` | Page ${pageIdx + 1} of ${totalPages}` : ''}
                </div>
            </div>
        </div>
    );
};

export default InvoicePrintPage;
